import { z } from 'zod';
import { refIdentityProviderTypeSchema } from '../enums/auth.js';

export const ClaimsPermissionMapping = z.record(z.string(), z.array(z.number()));
export type ClaimsPermissionMappingType = z.infer<typeof ClaimsPermissionMapping>;

export const ClaimsRolesMapping = z.record(z.string(), z.array(z.number()));
export type ClaimsRolesMappingType = z.infer<typeof ClaimsRolesMapping>;

export const ClaimsBrandMapping = z.record(z.string(), z.array(z.number()));
export type ClaimsBrandMappingType = z.infer<typeof ClaimsBrandMapping>;

/** Claim -> policy ids attached at login. */
export const ClaimsPoliciesMapping = z.record(z.string(), z.array(z.uuid()));
export type ClaimsPoliciesMappingType = z.infer<typeof ClaimsPoliciesMapping>;

export const identityProviderDataIConnectSchema = z.object({
  typeId: z.literal(refIdentityProviderTypeSchema.enum.VeevaIConnect),
  connectedAppIdentifier: z.string(),
  requiredClaims: z.array(z.string()).nullable().optional().prefault([]),
  claimsToPermissions: ClaimsPermissionMapping.nullable().optional().prefault({}),
  claimsToRoles: ClaimsRolesMapping.nullable().optional().prefault({}),
  claimsToPolicies: ClaimsPoliciesMapping.nullable().optional().prefault({}),
  claimsToBrands: ClaimsBrandMapping.nullable().optional().prefault({}),
});

export const identityProviderDataOIDCSchema = z.object({
  typeId: z.literal(refIdentityProviderTypeSchema.enum.OIDC),
  authorizationCallback: z.string(),
  connectedAppIdentifier: z.string(),
  requiredClaims: z.array(z.string()).nullable().optional().prefault([]),
  claimsToPermissions: ClaimsPermissionMapping.nullable().optional().prefault({}),
  claimsToRoles: ClaimsRolesMapping.nullable().optional().prefault({}),
  claimsToPolicies: ClaimsPoliciesMapping.nullable().optional().prefault({}),
  claimsToBrands: ClaimsBrandMapping.nullable().optional().prefault({}),
});
export type IdentityProviderDataOIDC = z.infer<typeof identityProviderDataOIDCSchema>;

export const identityProviderDataPWSchema = z.object({
  typeId: z.literal(refIdentityProviderTypeSchema.enum.UsernamePassword),
});

export const identityProviderDataM2MSessionSchema = z.object({
  typeId: z.literal(refIdentityProviderTypeSchema.enum.M2MSession),
});

export const identityProviderDataSentimentSchema = z.object({
  typeId: z.literal(refIdentityProviderTypeSchema.enum.Sentiment),
});

export const identityProviderDataSalesforceSchema = z.object({
  typeId: z.literal(refIdentityProviderTypeSchema.enum.Salesforce),
  requiredClaims: z.array(z.string()).nullable().optional().prefault([]),
  claimsToPermissions: ClaimsPermissionMapping.nullable().optional().prefault({}),
  claimsToRoles: ClaimsRolesMapping.nullable().optional().prefault({}),
  claimsToPolicies: ClaimsPoliciesMapping.nullable().optional().prefault({}),
  claimsToBrands: ClaimsBrandMapping.nullable().optional().prefault({}),
});

export const identityProviderDataTrinityEdgeSchema = z.object({
  typeId: z.literal(refIdentityProviderTypeSchema.enum.TrinityEdge),
  authorizationCallback: z.string(),
  requiredClaims: z.array(z.string()).nullable().optional().prefault([]),
  claimsToPermissions: ClaimsPermissionMapping.nullable().optional().prefault({}),
  claimsToRoles: ClaimsRolesMapping.nullable().optional().prefault({}),
  claimsToPolicies: ClaimsPoliciesMapping.nullable().optional().prefault({}),
  claimsToBrands: ClaimsBrandMapping.nullable().optional().prefault({}),
});
export type IdentityProviderDataTrinityEdge = z.infer<typeof identityProviderDataTrinityEdgeSchema>;

export const identityProviderDataSchema = z.discriminatedUnion('typeId', [
  identityProviderDataOIDCSchema,
  identityProviderDataIConnectSchema,
  identityProviderDataPWSchema,
  identityProviderDataM2MSessionSchema,
  identityProviderDataTrinityEdgeSchema,
  identityProviderDataSentimentSchema,
  identityProviderDataSalesforceSchema,
]);
export type IdentityProviderData = z.infer<typeof identityProviderDataSchema>;

/** The provider's claim -> policy mapping, or an empty mapping for provider types without claims. */
export function getClaimsToPolicies(data: IdentityProviderData | null | undefined): ClaimsPoliciesMappingType {
  if (!data || !('claimsToPolicies' in data)) return {};
  return data.claimsToPolicies ?? {};
}
